import { mq, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import i18n from "../../../i18n";
import { OrderContext } from "../../../pages/order/OrderContext";
import { useMinCheckPrice } from "../../../pages/order/hooks";
import { formatNullableCurrency } from "../../../utils/formatNullableCurrency";
import { useCanViewClientPortalPricing } from "../../permissions/Permissions";
import { useCheckCardContext } from "../CheckCardContext";
import { resolvePriceIsCatalogMinimumFromOptions } from "../../../utils/resolvePriceIsCatalogMinimum";
import { getAvailableSelectOptions } from "../../../pages/order/order-utils";

export function CheckCardTitle() {
  const {
    name,
    selectOptions,
    selectFieldEndpoint,
    testID,
    checkChildren,
    price,
    priceIsCatalogMinimum,
    currency,
    selectedOptions,
  } = useCheckCardContext();
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const optionsToEvaluate = selectedOptions?.length
    ? selectedOptions
    : getAvailableSelectOptions(selectOptions ?? []);
  const showCatalogMinimum = resolvePriceIsCatalogMinimumFromOptions(
    {
      price_is_catalog_minimum: priceIsCatalogMinimum,
      children: checkChildren,
    },
    optionsToEvaluate
  );
  const useFromText =
    !!selectOptions?.length || !!selectFieldEndpoint || showCatalogMinimum;
  const { order } = useContext(OrderContext);

  const priceNum = useMinCheckPrice(
    checkChildren || [],
    selectOptions || [],
    price,
    order.group,
    selectFieldEndpoint
  );

  const formattedPrice = formatNullableCurrency(priceNum, {
    currency: currency || "CAD",
    locale: i18n.language,
    fallback: null,
  });

  return (
    <div
      className={css(
        styles.nameAndPrice,
        mq({
          flexDirection: ["column", "row"],
        })
      )}
    >
      <Text as="h3" style="card/title-medium">
        {name}
      </Text>
      {canViewClientPortalPricing && formattedPrice && (
        <div className={styles.price}>
          {useFromText && <Text style="global/info-label">{t("From ")}</Text>}
          <Text
            style="global/heading-small"
            color="text-accent-tofino"
            testID={`${testID}-price-per-check`}
          >
            {formattedPrice}
          </Text>
          <Text style="global/info-label">/{t("check")}</Text>
        </div>
      )}
    </div>
  );
}

const styles = {
  nameAndPrice: css(
    {
      display: "flex",
      flexGrow: 2,
      justifyContent: "space-between",
      gap: "var(--space-2)",
      minHeight: "var(--space-8)",
      textAlign: "start",
    },
    mq({
      alignItems: ["start", "center"],
    })
  ),
  price: css({
    display: "flex",
    whiteSpace: "nowrap",
    alignItems: "center",
    gap: "var(--space-1)",
  }),
};
